import type { NewsletterSettings } from "./newsletterUtils";

export type EmailOnboardingRecommendedRoute = "classic_mailbox" | "amazon_ses";
export type EmailOnboardingGuardrailProfile = "demarrage" | "mode_progressif";
export type EmailOnboardingPreviousSendingRoute =
  | "none_yet"
  | "classic_mailbox"
  | "specialist_tool"
  | "amazon_ses"
  | "unknown";
export type EmailOnboardingSendSizeRange = "1_100" | "101_500" | "501_2000" | "2000_plus";
export type EmailOnboardingSendFrequency = "occasional" | "monthly" | "weekly" | "multi_weekly";
export type EmailOnboardingIntendedEmailType = "simple" | "newsletter_regular";

export type EmailOnboardingOption<T extends string> = {
  value: T;
  label: string;
  hint?: string;
};

export type EmailOnboardingRecommendation = {
  recommended_route: EmailOnboardingRecommendedRoute;
  initial_guardrail_profile: EmailOnboardingGuardrailProfile;
  can_continue_with_classic_mailbox: boolean;
  show_ses_cost_note: boolean;
  reason_codes: string[];
  summary_title: string;
  summary_message: string;
  summary_points: string[];
};

export const PREVIOUS_ROUTE_OPTIONS: EmailOnboardingOption<EmailOnboardingPreviousSendingRoute>[] = [
  {
    value: "none_yet",
    label: "Je n'envoyais pas encore vraiment ce type d'emails",
  },
  {
    value: "classic_mailbox",
    label: "J'envoyais depuis une boite mail classique",
  },
  {
    value: "specialist_tool",
    label: "J'utilisais deja un outil d'emailing",
  },
  {
    value: "amazon_ses",
    label: "J'utilisais deja Amazon SES",
  },
  {
    value: "unknown",
    label: "Je ne sais pas / autre",
  },
];

export const SEND_SIZE_OPTIONS: EmailOnboardingOption<EmailOnboardingSendSizeRange>[] = [
  { value: "1_100", label: "1 a 100" },
  { value: "101_500", label: "101 a 500" },
  { value: "501_2000", label: "501 a 2 000" },
  { value: "2000_plus", label: "Plus de 2 000" },
];

export const SEND_FREQUENCY_OPTIONS: EmailOnboardingOption<EmailOnboardingSendFrequency>[] = [
  { value: "occasional", label: "Occasionnellement" },
  { value: "monthly", label: "Environ 1 fois par mois" },
  { value: "weekly", label: "Environ 1 fois par semaine" },
  { value: "multi_weekly", label: "Plusieurs fois par semaine" },
];

export const INTENDED_EMAIL_TYPE_OPTIONS: EmailOnboardingOption<EmailOnboardingIntendedEmailType>[] = [
  { value: "simple", label: "Email simple" },
  { value: "newsletter_regular", label: "Newsletter reguliere" },
];

const REQUIRED_ONBOARDING_ANSWER_FIELDS = [
  "email_onboarding_previous_sending_route",
  "email_onboarding_previous_send_size_range",
  "email_onboarding_previous_send_frequency",
  "email_onboarding_intended_send_size_range",
  "email_onboarding_intended_send_frequency",
  "email_onboarding_intended_email_type",
] as const satisfies readonly (keyof NewsletterSettings)[];

function resolveOptionLabel<T extends string>(options: readonly EmailOnboardingOption<T>[], value: string, fallback: string) {
  return options.find((option) => option.value === value)?.label || fallback;
}

export function hasCompletedEmailOnboarding(settings: Partial<NewsletterSettings>): boolean {
  return REQUIRED_ONBOARDING_ANSWER_FIELDS.every((field) => Boolean(String(settings[field] || "").trim()));
}

export function getPreviousRouteLabel(value: string) {
  return resolveOptionLabel(PREVIOUS_ROUTE_OPTIONS, value, "A confirmer");
}

export function getSendSizeLabel(value: string) {
  return resolveOptionLabel(SEND_SIZE_OPTIONS, value, "A confirmer");
}

export function getSendFrequencyLabel(value: string) {
  return resolveOptionLabel(SEND_FREQUENCY_OPTIONS, value, "A confirmer");
}

export function getIntendedEmailTypeLabel(value: string) {
  return resolveOptionLabel(INTENDED_EMAIL_TYPE_OPTIONS, value, "A confirmer");
}

export function getRecommendedRouteLabel(value: string) {
  if (value === "amazon_ses") {
    return "Amazon SES";
  }
  if (value === "classic_mailbox") {
    return "Adresse email classique";
  }
  return "A confirmer";
}

export function getGuardrailProfileLabel(value: string) {
  if (value === "mode_progressif") {
    return "Mode progressif";
  }
  if (value === "demarrage") {
    return "Demarrage";
  }
  return "A confirmer";
}
